import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const delim = lines[0].includes(";") ? ";" : ",";
  const split = (line) => {
    const out = [];
    let cur = "", inQ = false;
    for (const ch of line) {
      if (ch === '"') inQ = !inQ;
      else if (ch === delim && !inQ) { out.push(cur.trim()); cur = ""; }
      else cur += ch;
    }
    out.push(cur.trim());
    return out;
  };
  const headers = split(lines[0]).map((h) => h.toLowerCase().replace(/[éè]/g, "e").replace(/\s+/g, "_"));
  return lines.slice(1).map((line) => {
    const cells = split(line);
    const row = {};
    headers.forEach((h, i) => { row[h] = cells[i] || ""; });
    if (row.courriel && !row.email) row.email = row.courriel;
    return row;
  });
}

export default function MemberImport() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState(null);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result));
      setRows(parsed);
      if (parsed.length === 0) toast.error("Fichier vide ou illisible (attendu : en-têtes email, prenom, nom)");
    };
    reader.readAsText(file, "utf-8");
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      setPreview(await api("/admin/membres/import/preview", { method: "POST", body: { rows } }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const res = await api("/admin/membres/import/commit", { method: "POST", body: { rows } });
      setResult(res);
      toast.success(`Import terminé : ${res.crees} membre(s) créé(s), ${res.ignores} ignoré(s)`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="member-import-page" className="space-y-6 animate-fade-in">
        <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Import CSV de membres</h1>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-4">
          <p className="text-sm text-cfhtb-text">
            Colonnes attendues : <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">email, prenom, nom, telephone, statut_pro, type</code>{" "}
            (séparateur <b>;</b> ou <b>,</b>). Les doublons d'email sont détectés avant import.
          </p>
          <div
            data-testid="admin-import-csv-dropzone"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
            className="cursor-pointer rounded-lg border-2 border-dashed border-cfhtb-light/60 bg-cfhtb-bg/40 hover:bg-cfhtb-bg transition-colors p-10 text-center"
          >
            <Upload className="h-8 w-8 mx-auto text-cfhtb-secondary" />
            <p className="text-sm font-medium text-cfhtb-heading mt-3">
              {fileName || "Glissez un fichier CSV ici, ou cliquez pour parcourir"}
            </p>
            <p className="text-xs text-cfhtb-text mt-1">{rows.length > 0 ? `${rows.length} ligne(s) détectée(s)` : "Format .csv, 1000 lignes max"}</p>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              data-testid="import-file-input"
              onChange={(e) => handleFile(e.target.files?.[0])} />
          </div>
          {rows.length > 0 && !preview && (
            <div className="flex justify-end">
              <button data-testid="import-preview-button" onClick={runPreview} disabled={busy}
                className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60">
                <FileSpreadsheet className="h-4 w-4" /> {busy ? "Analyse…" : "Prévisualiser l'import"}
              </button>
            </div>
          )}
        </div>

        {preview && !result && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="import-preview-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-200 bg-[#F7F7F7]">
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="inline-flex items-center gap-1.5 text-cfhtb-success"><CheckCircle2 className="h-4 w-4" /> {preview.valides} valides</span>
                <span className="inline-flex items-center gap-1.5 text-cfhtb-warning"><AlertTriangle className="h-4 w-4" /> {preview.doublons} doublons</span>
                <span className="inline-flex items-center gap-1.5 text-cfhtb-error"><XCircle className="h-4 w-4" /> {preview.erreurs} erreurs</span>
              </div>
              <button data-testid="import-commit-button" onClick={commit} disabled={busy}
                className="bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60">
                {busy ? "Import…" : `Importer ${preview.valides} membre(s)`}
              </button>
            </div>
            <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-[#F7F7F7] border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-2.5">Ligne</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-2.5">Nom</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-2.5">Email</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-cfhtb-heading px-4 py-2.5">Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.lignes.map((l) => (
                    <tr key={l.ligne} className={`border-b border-slate-100 text-sm ${
                      l.erreurs.length ? "bg-cfhtb-error/5" : l.doublon ? "bg-cfhtb-warning/5" : ""
                    }`} data-testid={`import-row-${l.ligne}`}>
                      <td className="px-4 py-2.5 text-cfhtb-text">{l.ligne}</td>
                      <td className="px-4 py-2.5 text-cfhtb-heading font-medium">{l.prenom} {l.nom}</td>
                      <td className="px-4 py-2.5 text-cfhtb-text">{l.email}</td>
                      <td className="px-4 py-2.5 text-xs">
                        {l.erreurs.length ? (
                          <span className="text-cfhtb-error font-medium">{l.erreurs.join(" · ")}</span>
                        ) : l.doublon ? (
                          <span className="text-cfhtb-warning font-medium">
                            Doublon : {l.doublon.raison}{l.doublon.membre ? ` (${l.doublon.membre})` : ""}
                          </span>
                        ) : (
                          <span className="text-cfhtb-success font-medium">Prêt à importer</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className="bg-white rounded-lg border border-cfhtb-success/30 shadow-sm p-6 text-center space-y-3" data-testid="import-result">
            <CheckCircle2 className="h-10 w-10 mx-auto text-cfhtb-success" />
            <p className="font-serif text-xl font-semibold text-cfhtb-heading">
              {result.crees} membre(s) créé(s), {result.ignores} ignoré(s)
            </p>
            <button data-testid="import-back-button" onClick={() => navigate("/admin/membres")}
              className="bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] transition-colors">
              Voir la liste des membres
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
